import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { analysisParameterError, fetchAnalysisParameterContracts } from '../../api/analysisParameters';
import type { AnalysisParameterDefaults } from '../../types';
import { parameterRange } from '../../utils/analysisParameterValidation';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../pilot-ui/table';

export default function AnalysisDefaultsPanel() {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('manage_analysis_runs');
  const [methods, setMethods] = useState<AnalysisParameterDefaults[]>([]);
  const [loading, setLoading] = useState(allowed);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!allowed) return;
    let current = true;
    setLoading(true);
    setError('');
    fetchAnalysisParameterContracts().then((response) => {
      if (current) setMethods(response.methods);
    }).catch((failure: unknown) => {
      if (current) setError(analysisParameterError(failure));
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [allowed]);
  if (!allowed) return <p>Viewing analysis defaults requires the <code>manage_analysis_runs</code> permission.</p>;
  if (loading) return <p role="status">Loading analysis defaults…</p>;
  if (error) return <p role="alert">{error}</p>;
  return <div className="space-y-6">{methods.map((method) => (
    <article key={method.analysis_key} className="space-y-2">
      <h3 className="font-semibold">{method.display_name}</h3>
      <p>Handler: <code>{method.handler_key}</code>{method.contract && <> · Contract: <code>{method.contract.version}</code></>}</p>
      {method.contract ? <>
        <Table aria-label={`${method.display_name} defaults`}>
          <TableHeader><TableRow>{['Parameter', 'Kind', 'Allowed range', 'Code default', 'Method default', 'Effective default'].map((label) => <TableHead key={label}>{label}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{method.contract.parameters.map((parameter) => {
            const methodDefault = method.definition_defaults[parameter.key];
            return <TableRow key={parameter.key}>
              <TableCell className="font-mono">{parameter.key}</TableCell><TableCell>{parameter.kind}</TableCell>
              <TableCell>{parameterRange(parameter)}</TableCell><TableCell>{JSON.stringify(parameter.default)}</TableCell>
              <TableCell>{methodDefault !== undefined && methodDefault !== parameter.default ? JSON.stringify(methodDefault) : '—'}</TableCell>
              <TableCell>{JSON.stringify(method.effective_defaults?.[parameter.key])}</TableCell>
            </TableRow>;
          })}</TableBody>
        </Table>
        {method.contract.combination_rules.map((rule) => <p key={rule} className="text-sm text-muted-foreground">{rule}</p>)}
      </> : <>
        <p>This method&apos;s parameters are not published by the analysis code yet.</p>
        <p>Database defaults:</p><pre>{JSON.stringify(method.definition_defaults, null, 2)}</pre>
      </>}
    </article>
  ))}</div>;
}
